import type { ErrorEvent } from "@sentry/react";
import { isAuthNetworkError } from "@/lib/authNetworkError";
import { isTransportFailureMessage } from "@/lib/transportFailure";

/**
 * Is this failure a dropped connection, and did `reportError` already decide
 * not to page about it? Issue #1811.
 *
 * ── What is being fixed ────────────────────────────────────────────────────
 *
 * Sentry JAVASCRIPT-REACT-2J/2K/2M/2N (Sep 29 – Oct 2 2026, production) are
 * WebKit "Load failed" transport drops reaching #bugs as if they were defects.
 * Nothing failed in the app: the phone slept, the signal went, the request
 * never completed. The UI already handles it correctly — `GroceryList`'s
 * `toggleCheck` snaps the checkbox back and toasts — so the only wrong thing
 * is the page. #1709 fixed this class for chat titles only.
 *
 * A dropped connection is still worth KEEPING, just not worth waking anyone:
 * it becomes a breadcrumb, so when a real error does arrive, the drops that
 * led up to it are sitting right there on the event.
 *
 * ── Where `status` actually lives ──────────────────────────────────────────
 *
 * ⚠️ The issue describes shape 2 as "PostgREST object with `status === 0`".
 * Measured on main, that field is on the postgrest-js **response**, not on the
 * error: `const { data, error } = await supabase.from(…)` destructures an
 * error with `message`, `code`, `details`, `hint` and **no `status` at all`
 * (`transportFailure.ts` and `createConversationWithRetry.ts:28-47` both
 * record this against a real client). Every one of the ~190 `reportError`
 * call sites passes that bare `error`.
 *
 * So a `status === 0` check alone would have returned false on all four of the
 * Sentry issues this exists for. It is still checked, because a caller holding
 * a whole response gets an exact answer from it and that is cheap — but the
 * detector that actually fires on a PostgREST write is the MESSAGE one, and
 * `transportFailure.ts`'s "don't route PostgREST through here" warning is
 * about preferring `status` when you HAVE it, not about a case where you don't.
 *
 * ── The console tap is the other half ──────────────────────────────────────
 *
 * ⚠️ Returning early before `Sentry.captureException` is NOT enough to stop
 * the page. `captureConsoleIntegration` makes every `console.error` its own
 * event, and per #1255 that console event is the one that reaches #bugs
 * first. 2K and 2M are the same tap reported twice. So the drop is marked on
 * the scope *before* the log runs, and `isTransportDropEvent` recognises the
 * mark in `beforeSend`. The console OUTPUT is unchanged — a dropped request
 * still prints in a Web Inspector attached to her phone, which is where #1709
 * and the on-device playbook both need it.
 */

/**
 * Scope tag marking a console line that `reportError` has already classified
 * as a transport drop. Read in `beforeSend` and nowhere else.
 */
export const TRANSPORT_DROP_TAG = "transport_drop";

/** Breadcrumb category for a drop that was recorded instead of captured. */
export const TRANSPORT_BREADCRUMB_CATEGORY = "transport";

/**
 * True when `cause` is a dropped connection rather than a failure the app is
 * responsible for.
 *
 * Covers `transportFailure.ts`'s three shapes, in the order that answers
 * fastest with the least guessing:
 *
 *   1. **auth / GoTrue** — `isAuthNetworkError`, which owns `status === 0` on
 *      an `AuthRetryableFetchError` and the `AbortError` case.
 *   2. **a whole postgrest-js response** — `status === 0`, set in exactly one
 *      place: the catch for a rejected fetch. Exact, and locale-independent.
 *   3. **a message string** — a bare string, an `Error`, or the `message` of a
 *      PostgREST error object. Last resort and browser-dependent, which is
 *      why it is last, but it is the one that fires in production.
 *
 * ⚠️ Everything else is a real failure and must still page. In particular
 * `reportError("…", error)` is called with `cause === null` on the RLS
 * zero-row path (`GroceryList.tsx:461` — `error || !data?.length`), and a
 * `42501` carries a message this does not match. Both return false here.
 */
export function isTransportDrop(cause: unknown): boolean {
  if (typeof cause === "string") return isTransportFailureMessage(cause);
  if (typeof cause !== "object" || cause === null) return false;

  if (isAuthNetworkError(cause)) return true;

  const { status, message } = cause as { status?: unknown; message?: unknown };
  if (status === 0) return true;

  return isTransportFailureMessage(message);
}

/**
 * True for an event `reportError` already recorded as a drop — in practice the
 * one `captureConsoleIntegration` synthesises from its `console.error`.
 *
 * Kept separate from `repairConsoleEventTitle` on purpose: that hook's
 * contract is "adds nothing, drops nothing", it is documented that way, and
 * its tests assert it. Composing two hooks in `main.tsx` keeps that true
 * instead of quietly turning a titling fix into a filter.
 */
export function isTransportDropEvent(event: ErrorEvent): boolean {
  return event.tags?.[TRANSPORT_DROP_TAG] === "true";
}
